import { isIgnoredDomain } from '../../domain/company';
import { domainIgnoreList } from '../../domain/ignoredDomains';
import type { CollectedDataItem } from '../../domain/message';
import { CountryService } from '../country/country';
import UrlService from '../UrlService';

const createSharedDataExtractionService = () => {
  const extractName = (collectedData: CollectedDataItem[]): string | undefined => {
    const name = collectedData.find((item) => item.name === 'Full Name')?.content;
    return typeof name === 'string' ? name : name?.join(' ');
  };

  const extractTitle = (collectedData: CollectedDataItem[]): string | undefined => {
    const title = collectedData.find((item) => item.name === 'Title')?.content;
    return typeof title === 'string' ? title : title?.join(' ');
  };

  const extractCountry = (collectedData: CollectedDataItem[]) => {
    const location = collectedData.find((item) => item.name === 'Location')?.content;
    if (!location) {
      return undefined;
    }

    const possibleCountry = (typeof location === 'string' && location.split(', ').at(-1)!.trim()) || '';
    return CountryService.suggestCountry(possibleCountry)?.text;
  };

  const extractContentByKey = (collectedData: CollectedDataItem[], key: string): string | string[] | undefined => {
    return collectedData.find((item) => item.name === key)?.content;
  };

  const extractCompanyName = (collectedData: CollectedDataItem[]): string => {
    const companyElem = collectedData.find(({ name }) => name === 'Company Name');
    return (typeof companyElem?.content === 'string' && companyElem.content) || '';
  };

  const extractCompanyDomain = (collectedData: CollectedDataItem[]): string | undefined => {
    const websiteElem = collectedData.find(({ name }) => name === 'Company Website');
    if (!websiteElem || !websiteElem.attributes?.['href'] || typeof websiteElem.attributes['href'] !== 'string') {
      return;
    }

    try {
      const { hostname } = new URL(websiteElem.attributes['href']);
      return UrlService.removeWWW(hostname);
    } catch {
      return;
    }
  };

  const extractAttributeValueByKey = <T = string>(
    collectedData: CollectedDataItem[],
    key: string,
    attributeKey: string
  ): T | undefined => {
    return collectedData.find((item) => item.name === key)?.attributes?.[attributeKey] as T | undefined;
  };

  const extractCompanyDomainFromEmail = (email: string | undefined): string | undefined => {
    const atIndex = email?.indexOf('@') ?? -1;
    if (atIndex === -1) {
      return undefined;
    }

    return email?.slice(atIndex + 1);
  };

  const isNotIgnoredDomain = (domain: string): boolean =>
    !isIgnoredDomain(domain) && !domainIgnoreList.includes(domain);

  const unique = <T extends string>(array: T[]): T[] => Array.from(new Set(array));

  const uniqueAndSorted = (domains: string[]): string[] => unique(domains).sort();

  const getDomainsFromEmails = (emails: string[], withSorting = true) => {
    const domains = emails
      .filter((email) => email?.includes('@'))
      .map(extractCompanyDomainFromEmail)
      .map((domain) => domain!.toLowerCase())
      .filter(UrlService.isValidUrl)
      .filter(isNotIgnoredDomain);

    return withSorting ? uniqueAndSorted(domains) : unique(domains);
  };

  return {
    extractName,
    extractTitle,
    extractCountry,
    extractContentByKey,
    extractCompanyName,
    extractCompanyDomain,
    extractAttributeValueByKey,
    extractCompanyDomainFromEmail,
    isNotIgnoredDomain,
    uniqueAndSorted,
    getDomainsFromEmails,
  };
};

const SharedDataExtractionService = createSharedDataExtractionService();

export default SharedDataExtractionService;
